import { useEffect, useState } from 'react';
import { useCompany } from '../App';
import { api } from '../lib/api';
import { useAsync } from '../lib/useAsync';
import { Badge, Button, Card, DataTable, ErrorBanner, Select, Spinner, StatTile } from '../components/ui';
import { dateTime } from '../lib/format';

// Every entity_type actually written by an audit.record() call anywhere in
// this codebase -- 'exercise', 'invitation' and 'proposal' were missing
// here, so filtering the log to just those events was never possible even
// though every one of them shows up under "All entities".
const ENTITY_TYPES = [
  'company', 'stakeholder', 'shareClass', 'equityPlan', 'vestingSchedule', 'security',
  'round', 'document', 'valuation', 'exercise', 'invitation', 'proposal',
];
const PAGE_SIZE = 200;

/**
 * The register's own audit-log route supports `before` (a row id cursor) for
 * exactly this reason, but until now nothing here ever passed it -- every
 * page fetched only the most recent PAGE_SIZE rows with no way to see
 * anything older, silently truncating what is meant to be a complete
 * append-only history the moment a company's history passed that count.
 */
export default function AuditLog() {
  const { companyId, role } = useCompany();
  const [entityType, setEntityType] = useState('');
  const [entries, setEntries] = useState([]);
  const [hasMore, setHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [moreError, setMoreError] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState(null);

  const log = useAsync(
    () => api.auditLog(companyId, { limit: PAGE_SIZE, ...(entityType ? { entityType } : {}) }),
    [companyId, entityType]
  );
  const check = useAsync(
    () => (role === 'ADMIN' || role === 'OWNER' ? api.reconcile(companyId) : Promise.resolve(null)),
    [companyId, role]
  );

  // A fresh first page (a filter change or Refresh) replaces the whole
  // list rather than appending -- pagination state starts over with it.
  useEffect(() => {
    if (!log.data) return;
    setEntries(log.data);
    setHasMore(log.data.length === PAGE_SIZE);
    setMoreError(null);
  }, [log.data]);

  async function loadMore() {
    setLoadingMore(true);
    setMoreError(null);
    try {
      const before = entries[entries.length - 1]?.id;
      const next = await api.auditLog(companyId, {
        limit: PAGE_SIZE,
        before,
        ...(entityType ? { entityType } : {}),
      });
      setEntries((prev) => [...prev, ...next]);
      setHasMore(next.length === PAGE_SIZE);
    } catch (err) {
      setMoreError(err);
    } finally {
      setLoadingMore(false);
    }
  }

  async function exportLog() {
    setExporting(true);
    setExportError(null);
    try {
      await api.exportAuditLog(companyId, entityType ? { entityType } : {});
    } catch (err) {
      setExportError(err);
    } finally {
      setExporting(false);
    }
  }

  return (
    <div className="stack">
      <div className="page-head">
        <div>
          <h1>Audit log</h1>
          <p>
            Append-only — the database rejects any update or delete. Each entry is written in
            the same transaction as the change it describes, so a gap means a rolled-back
            change rather than a lost log line.
          </p>
        </div>
        <div className="page-actions">
          <Select value={entityType} onChange={(e) => setEntityType(e.target.value)} style={{ width: 180 }}>
            <option value="">All entities</option>
            {ENTITY_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
          </Select>
          <Button onClick={log.reload}>Refresh</Button>
          <Button loading={exporting} onClick={exportLog}>Download as spreadsheet</Button>
        </div>
      </div>

      <ErrorBanner error={exportError} onRetry={exportLog} />

      {check.data ? (
        <div className="grid grid-stats">
          <StatTile
            label="Ledger reconciliation"
            value={check.data.consistent ? 'Consistent' : 'MISMATCH'}
            tone={check.data.consistent ? 'good' : 'critical'}
            sub={
              check.data.consistent
                ? 'Cached balances equal the sum of the ledger'
                : `${check.data.discrepancies.length} securities disagree`
            }
          />
        </div>
      ) : null}

      {log.loading ? <Spinner /> : log.error ? <ErrorBanner error={log.error} onRetry={log.reload} /> : (
        <Card padded={false}>
          <DataTable
            rowKey={(r) => r.id}
            empty="Nothing recorded yet."
            columns={[
              { key: 'created_at', header: 'When', render: (r) => dateTime(r.created_at) },
              { key: 'action', header: 'Action', render: (r) => <span className="primary-cell">{r.action}</span> },
              { key: 'entity_type', header: 'Entity', render: (r) => <Badge tone="neutral">{r.entity_type}</Badge> },
              { key: 'actor_email', header: 'Actor', render: (r) => r.actor_email ?? '—' },
              {
                key: 'entity_id', header: 'Id',
                render: (r) => <code style={{ fontSize: 11, color: 'var(--text-muted)' }}>{String(r.entity_id ?? '').slice(0, 8)}</code>,
              },
              {
                key: 'request_id', header: 'Request',
                render: (r) => <code style={{ fontSize: 11, color: 'var(--text-muted)' }}>{String(r.request_id ?? '—').slice(0, 8)}</code>,
              },
            ]}
            rows={entries}
          />
          <ErrorBanner error={moreError} onRetry={loadMore} />
          {hasMore ? (
            <div style={{ padding: 16, textAlign: 'center' }}>
              <Button loading={loadingMore} onClick={loadMore}>Load older entries</Button>
            </div>
          ) : null}
        </Card>
      )}
    </div>
  );
}
